import type { LearnTopic } from "../topic-types";

/**
 * Trauma & Sleep.
 *
 * Add new guides, exercises, prompts or worksheets by appending to a
 * section's `resources` array — no page or route changes required.
 */
export const TRAUMA_SLEEP_TOPIC: LearnTopic = {
  slug: "trauma-sleep",
  emoji: "🌙",
  title: "Trauma & Sleep",
  tagline: "Rest when nights feel unsafe",
  theme: "sky",
  category: "sleep",
  seoDescription:
    "Understand why trauma can disrupt sleep, learn grounding techniques for nightmares and night-time hyperarousal, and build a calming bedtime routine. Trauma-informed guides, exercises and worksheets from Recovery Trauma™.",
  cardBlurb: "Understanding hyperarousal, nightmares, and building a calmer bedtime routine.",
  isNew: true,
  intro: [
    "For many people who have lived through difficult or frightening experiences, night-time can feel far from restful. The body's alarm system, tuned by past events, can make it hard to switch off, and sleep itself can sometimes feel unsafe rather than restorative.",
    "This might show up as lying awake scanning for danger, nightmares that leave you shaken, waking suddenly and not knowing why, or dreading bedtime altogether. None of this means something is wrong with you — it means your nervous system learned, at some point, that unguarded rest wasn't safe.",
    "This module looks at why trauma and sleep are so closely connected, and offers gentle, practical ways to build more safety around bedtime — alongside honest information about when it may help to bring in extra support.",
  ],
  closing:
    "Sleep difficulties after trauma are common and understandable, not a personal failing. If nightmares, insomnia or night-time fear are persistent, exhausting or affecting your daily life, a GP, sleep specialist or trauma-informed therapist can offer support tailored to you — you don't have to manage this alone.",
  sections: [
    {
      slug: "understand",
      emoji: "📚",
      title: "Understanding trauma and sleep",
      blurb: "Why nights can feel harder, and what's happening underneath.",
      intro:
        "Plain-English education first. There is nothing to do yet except read, at whatever pace feels manageable.",
      resources: [
        {
          slug: "why-nights-feel-different",
          kind: "learn",
          title: "Why trauma can make nights feel different",
          summary: "Hyperarousal, vigilance, and a nervous system that struggles to switch off.",
          minutes: 5,
          blocks: [
            {
              type: "prose",
              text: "Sleep requires the nervous system to feel safe enough to let its guard down. For someone whose alarm system has been shaped by trauma, that letting-go can feel risky rather than restful — the body may stay on alert precisely when it's meant to be winding down.",
            },
            {
              type: "list",
              title: "This can show up as",
              items: [
                "Hyperarousal — feeling wired, restless or “switched on” at bedtime",
                "Difficulty switching off a racing mind, even when exhausted",
                "Hypervigilance — checking locks, listening for sounds, needing lights or doors a certain way",
                "Sleeping lightly, or waking at the smallest noise",
                "A general sense that being unconscious and unguarded feels unsafe",
              ],
            },
            {
              type: "note",
              text: "These responses made sense at some point — often when going into a deep, undefended sleep genuinely wasn't safe. Understanding this can reduce some of the frustration or self-blame that builds up around poor sleep.",
            },
          ],
        },
        {
          slug: "nightmares-and-waking-frightened",
          kind: "learn",
          title: "Nightmares and waking frightened",
          summary: "Why trauma-related nightmares happen, and how they differ from ordinary bad dreams.",
          minutes: 5,
          blocks: [
            {
              type: "prose",
              text: "Nightmares after trauma can replay events directly, or can carry similar feelings — fear, helplessness, being chased or trapped — without matching what actually happened. Either way, they can leave you waking with a racing heart, sweating, or feeling unsafe even once you're awake.",
            },
            {
              type: "list",
              title: "Common experiences",
              items: [
                "Nightmares that repeat a similar theme or feeling, night after night",
                "Waking suddenly with a strong startle response",
                "Confusion for a few moments about where or “when” you are",
                "Reluctance to fall back asleep afterwards",
                "Fatigue and irritability building up over time",
              ],
            },
            {
              type: "prose",
              text: "Nightmares are not a sign of weakness or a failure to “process” trauma correctly. They are a common, well-documented response, and there are gentle techniques — as well as professional treatments — that can help.",
            },
          ],
        },
        {
          slug: "feeling-unsafe-at-night",
          kind: "learn",
          title: "Feeling unsafe at night",
          summary: "Why darkness, stillness and being alone can amplify unease.",
          minutes: 4,
          blocks: [
            {
              type: "prose",
              text: "Night-time often removes many of the things that help a nervous system feel oriented and safe during the day — light, activity, other people, distraction. Without those anchors, difficult feelings and vigilance can rise to the surface.",
            },
            {
              type: "list",
              title: "You might notice",
              items: [
                "A sense of unease that only appears once it's dark or quiet",
                "Needing certain conditions to feel safe enough to sleep (lights on, doors checked, a particular position)",
                "Feeling more alone or exposed once others have gone to bed",
                "Racing thoughts that seem to wait until night-time to appear",
              ],
            },
            {
              type: "note",
              text: "None of these responses are irrational — they are your nervous system doing its best with the information and history it has.",
            },
          ],
        },
      ],
    },
    {
      slug: "recognise",
      emoji: "🔎",
      title: "Recognising your own patterns",
      blurb: "Gentle noticing of what your nights actually look like.",
      resources: [
        {
          slug: "noticing-your-sleep-patterns",
          kind: "reflect",
          title: "Noticing your sleep patterns",
          summary: "Reflection prompts to understand your own night-time experience.",
          minutes: 6,
          blocks: [
            {
              type: "prompts",
              items: [
                "What does my mind or body tend to do in the hour before bed?",
                "Do I notice hypervigilance — checking, listening, needing things a certain way?",
                "Are there recurring themes or feelings in my nightmares, if I have them?",
                "What helps, even slightly, when I wake up frightened?",
                "What time of night, or type of night, tends to be hardest?",
              ],
            },
          ],
        },
        {
          slug: "when-to-seek-support",
          kind: "learn",
          title: "When professional support may help",
          summary: "Persistent nightmares and insomnia are treatable — you don't have to manage them alone.",
          minutes: 4,
          blocks: [
            {
              type: "prose",
              text: "Self-help strategies can genuinely help many people, but some sleep difficulties benefit from additional support. This isn't a sign that self-help has failed — sleep is a medical and psychological area with real, evidence-based treatments.",
            },
            {
              type: "list",
              title: "It may be worth speaking to a GP, sleep specialist or trauma-informed therapist if",
              items: [
                "Nightmares are frequent, distressing, or affecting your daytime functioning",
                "Insomnia has lasted for weeks or months",
                "You're relying heavily on alcohol or other substances to fall asleep",
                "Sleep difficulties are affecting your safety, mood, or relationships",
                "You'd simply like more support than self-help alone can offer",
              ],
            },
            {
              type: "note",
              text: "A GP can discuss options including sleep-focused therapies and, where appropriate, referrals to specialist services. This page can't recommend or prescribe treatment, but reaching out is a reasonable and often helpful step.",
            },
          ],
        },
      ],
    },
    {
      slug: "practise",
      emoji: "🌿",
      title: "Practices for calmer nights",
      blurb: "Gentle, practical steps for winding down and recovering after nightmares.",
      intro:
        "These are optional and can be adapted to fit your life. There's no pressure to do all of them, or to do them perfectly.",
      resources: [
        {
          slug: "wind-down-routine",
          kind: "practice",
          title: "Building a calming wind-down routine",
          summary: "A step-by-step approach to signalling safety to your nervous system before bed.",
          minutes: 10,
          blocks: [
            {
              type: "prose",
              text: "A wind-down routine works by giving your nervous system repeated, predictable signals that it's safe to slow down. Consistency matters more than any single step — small, repeated cues tend to help more than one big change.",
            },
            {
              type: "steps",
              title: "A gentle wind-down sequence",
              items: [
                { label: "Set a consistent “start winding down” time", note: "Even 30–45 minutes before you intend to sleep." },
                { label: "Dim lights and lower noise", note: "Signals to the body that the day is ending." },
                { label: "Do a safety check once, then let it be enough", note: "Locks, windows, whatever helps — done once, not on repeat." },
                { label: "Choose a calming, low-stimulation activity", note: "Reading, gentle stretching, a warm drink, quiet music." },
                { label: "Try a short grounding or breathing practice", note: "See our Grounding and Breathing tools." },
                { label: "Keep screens and distressing content out of the last stretch before bed", note: "News, intense shows or scrolling can raise alertness." },
                { label: "Get into bed only when drowsy, not simply “on schedule”", note: "This helps the body associate bed with rest, not wakefulness." },
              ],
            },
            {
              type: "note",
              text: "If your living situation makes some of these steps difficult or unsafe, adapt what you can and be kind to yourself about the rest.",
            },
          ],
        },
        {
          slug: "grounding-after-nightmares",
          kind: "practice",
          title: "Grounding after a nightmare",
          summary: "A short practice for the moments after waking frightened.",
          minutes: 5,
          blocks: [
            {
              type: "prose",
              text: "The goal here isn't to fall straight back to sleep — it's to help your body register that you are safe, awake, and in the present, before sleep is asked of it again.",
            },
            {
              type: "steps",
              title: "Steps to try after waking from a nightmare",
              items: [
                { label: "Name where and when you are", note: "“I am in my bed, in [year], it is [day]. That was a dream.”" },
                { label: "Orient with your senses", note: "Look around the room, name a few things you can see and hear." },
                { label: "Feel something physical", note: "Your feet on the mattress, a blanket's texture, cool water on your hands." },
                { label: "Slow your breathing", note: "A longer exhale than inhale can help settle the body." },
                { label: "Offer yourself a brief, kind statement", note: "“That was frightening, and it's over now. I'm safe in this room.”" },
                { label: "Only try to sleep again once you feel a little steadier", note: "It's okay if this takes a while." },
              ],
            },
            {
              type: "note",
              text: "For more grounding techniques, our Grounding tool has a range of exercises you can return to any time, day or night.",
            },
          ],
        },
        {
          slug: "sleep-safety-worksheet",
          kind: "worksheet",
          title: "Sleep and safety worksheet",
          summary: "A printable page for planning what helps you feel safer at bedtime and after waking.",
          minutes: 10,
          blocks: [
            {
              type: "prose",
              text: "Fill this in when you have a calm moment, so it's ready to use on harder nights.",
            },
          ],
          worksheetFields: [
            { label: "What tends to make bedtime harder for me", lines: 3 },
            { label: "Signals my body gives when it's on alert at night", lines: 2 },
            { label: "My wind-down routine (what I'll actually try)", lines: 3 },
            { label: "What helps me feel safe in my room or bed", lines: 2 },
            { label: "My plan for after a nightmare or frightened waking", lines: 3 },
            { label: "People or services I could reach out to if sleep stays difficult", lines: 2 },
          ],
        },
      ],
    },
    {
      slug: "reflect",
      emoji: "✍️",
      title: "Reflect",
      blurb: "Journal prompts for exploring your relationship with rest and night-time.",
      resources: [
        {
          slug: "reflecting-on-rest",
          kind: "reflect",
          title: "My relationship with rest",
          summary: "Longer-term reflection prompts.",
          minutes: 6,
          blocks: [
            {
              type: "prompts",
              items: [
                "When did nights start feeling difficult for me — is there a period I can trace it back to?",
                "What would “feeling safe enough to sleep” actually look or feel like?",
                "What is one small change that has helped, even slightly?",
                "What do I wish I could tell myself in the middle of a hard night?",
                "Is there support I've been putting off reaching out for?",
              ],
            },
            { type: "quote", text: "Rest is not something you have to earn by feeling calm first — it can be built, gently, one night at a time." },
          ],
        },
      ],
    },
  ],
  continueTo: [
    { label: "Grounding", to: "/grounding" },
    { label: "Breathing", to: "/breathing" },
    { label: "Window of Tolerance", to: "/learn/window-of-tolerance" },
    { label: "Emotional Flashbacks", to: "/learn/emotional-flashbacks" },
    { label: "Journal", to: "/journal" },
  ],
};
